import './manageHeaders.scss';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import { cloneDeep, map } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import { Form } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';

import { RemoveIcon } from '../../../assets/icons';
import FormField from '../../../lib/components/form/formField';
import { required, setFormArray } from '../../../lib/utils/form';
import { loadHeaders, saveHeaders } from '../../../store/asyncActions/headers';
import { displayActions } from '../../../store/slices/display';

/* eslint-disable no-underscore-dangle */

const RequestHederFormField = ({ index, enabled, removeHeader }) => {

    const _removeHeader = () => removeHeader(index);

    return (
        <tr className="request-header">
            <td>
                <FormField
                    id={`enabled-${index}`}
                    name={`requestHeaders[${index}].enabled`}
                    label=''
                    type="checkbox"
                />
            </td>
            <td className="header-name">
                <FormField
                    id={`name-${index}`}
                    name={`requestHeaders[${index}].name`}
                    label=''
                    required
                    validate={required}
                    maxLength={1000000000}
                    disabled={!enabled}
                />
            </td>
            <td className="header-value">
                <FormField
                    id={`value-${index}`}
                    name={`requestHeaders[${index}].value`}
                    label=''
                    required
                    validate={required}
                    maxLength={1000000000}
                    disabled={!enabled}
                />
            </td>
            <td>
                <Button className="remove-header-button" onClick={_removeHeader}>
                    <RemoveIcon />
                </Button>
            </td>
        </tr>
    );
};

RequestHederFormField.propTypes = {
    index: PropTypes.number.isRequired,
    enabled: PropTypes.bool.isRequired,
    removeHeader: PropTypes.func.isRequired,
};

const ManageRequestHeadersDialog = () => {
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(loadHeaders());
    }, [dispatch]);

    const open = useSelector(({ display }) => display.manageHeadersDialog);
    const isSubmitting = useSelector(({ workspace }) => workspace.isSubmitting);

    const requestHeaders = useSelector(({ workspace }) => workspace.requestHeaders);
    const intialValues = useMemo(() => ({ requestHeaders: Array.isArray(requestHeaders) ? cloneDeep(requestHeaders) : [] }), [requestHeaders]);

    const toggleManageRequestHeaders = () =>
        dispatch(displayActions.TOGGLE_MANAGE_REQUEST_HEADERS_DIALOG());

    const submitForm = formData => {
        dispatch(saveHeaders(formData.requestHeaders));
    };

    return (
        <Dialog
            disableBackdropClick
            open={open}
            onClose={toggleManageRequestHeaders}
            aria-labelledby='Manage Request Headers Dialog'
            id='manage_request_headers_dialog'
        >
            <DialogTitle>Manage Request Headers</DialogTitle>

            <DialogContent>
                <Form
                    onSubmit={submitForm}
                    initialValues={intialValues}
                    mutators={{
                        setFormArray
                    }}
                    render={({ form, values, handleSubmit, invalid }) => {
                        const addHeader = () => {
                            const newRequestHeaders = [...(values.requestHeaders || []), { name: null, value: null, enabled: true }];
                            form.mutators.setFormArray('requestHeaders', newRequestHeaders);
                        };
                        const removeHeader = (index) => {
                            const newRequestHeaders = [...values.requestHeaders];
                            newRequestHeaders.splice(index, 1);
                            form.mutators.setFormArray('requestHeaders', newRequestHeaders);
                        };
                        return (
                            <form onSubmit={handleSubmit} noValidate>
                                {!!values.requestHeaders?.length &&
                                    <table>
                                        <thead>
                                            <tr>
                                                <td />
                                                <td>Name</td>
                                                <td>Value</td>
                                                <td />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {map(values.requestHeaders, (header, index) => <RequestHederFormField
                                                key={index}
                                                index={index}
                                                enabled={header.enabled}
                                                removeHeader={removeHeader}
                                            />)
                                            }
                                        </tbody>
                                    </table>
                                }
                                <Button onClick={addHeader} className="add-header-button">Add</Button>
                                <DialogActions>
                                    <Button color='secondary' onClick={toggleManageRequestHeaders}>
                                        Cancel
                                    </Button>
                                    <Button color='primary' disabled={invalid || isSubmitting} onClick={handleSubmit}>
                                        Save
                                    </Button>
                                </DialogActions>
                            </form>
                        );
                    }}
                />
            </DialogContent>
        </Dialog>
    );
};

export default ManageRequestHeadersDialog;